import React, { useEffect, useState } from "react";
import DashLoading from "../components/DashLoading";
import { useIstToday } from "../lib/istDate";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { DashHero, DashCaption, Section, Tile, TileGrid, useDashImage, n, rupees } from "../components/TempleDash";
import {
  Landmark, Users, IndianRupee, TicketCheck, Stamp, CheckCircle2, ScanLine, ListChecks, UserCheck, Gift, CalendarClock, CalendarCheck2, Sparkles
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

const AX = { fontSize: 11, fill: "#64748B" };
const GRID = <CartesianGrid strokeDasharray="3 3" stroke="#F0E6D2" vertical={false} />;
const TONE_CYCLE = ["blue", "green", "yellow", "purple", "red", "orange"];
const find = (rows, name) => (rows || []).find((r) => String(r.name).toUpperCase() === name)?.value || 0;

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const { url, temple } = useDashImage("PROTOCOL", "", user);

  const load = () => api.get("/dashboard/stats").then((r) => { setData(r.data); setError(null); }).catch((e) => setError(e));
  const istDay = useIstToday(); // new day at 00:00 IST: reload today's figures at once
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [istDay]); // eslint-disable-line

  if (!data) return <DashLoading error={error} onRetry={() => { setError(null); load(); }} testid="dashboard-loading" />;
  const { stats, charts } = data;
  const isSA = user.role === "SUPER_ADMIN";
  const darshan = [...(charts.darshan_wise || [])].sort((a, b) => b.value - a.value).slice(0, 5);
  const byPlace = isSA ? charts.temple_wise || [] : charts.shift_wise || [];

  return (
    <div className="space-y-4" data-testid="dashboard-page">
      <DashHero image={url} testid="protocol-hero"
        left={[
          { icon: ListChecks, label: "Today's Transactions", value: n(stats.todays_transactions), testid: "stat-today-txn" },
          { icon: UserCheck, label: "Today's Devotees", value: n(stats.todays_devotees), testid: "stat-today-devotees" },
          { icon: Stamp, label: "Pending (Today)", value: n(stats.pending_approvals), testid: "stat-pending" },
          { icon: CheckCircle2, label: "Approved Today", value: n(stats.approved), testid: "stat-approved" },
        ]}
        right={[
          { icon: TicketCheck, label: "Tickets Issued Today", value: n(stats.issued), testid: "stat-issued" },
          { icon: ScanLine, label: "Darshan Completed Today", value: n(stats.completed), testid: "stat-completed" },
          { icon: IndianRupee, label: "Revenue Today", value: rupees(stats.revenue), testid: "stat-revenue" },
          isSA
            ? { icon: Landmark, label: "Active Temples", value: `${stats.active_temples}/${stats.total_temples}`, testid: "stat-temples" }
            : { icon: Users, label: "Total Users", value: n(stats.total_users), testid: "stat-users" },
        ]} />

      <DashCaption temple={temple} title="Protocol Darshan Dashboard" />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <Section title="Booking Status (Today)">
          <TileGrid cols={3}>
            <Tile icon={Stamp} label="Pending" value={n(stats.pending_approvals)} tone="yellow" />
            <Tile icon={CheckCircle2} label="Approved" value={n(stats.approved)} tone="blue" />
            <Tile icon={ScanLine} label="Completed" value={n(stats.completed)} tone="green" />
          </TileGrid>
        </Section>
        <Section title="Paid / Free (Today)">
          <TileGrid cols={3}>
            <Tile icon={IndianRupee} label="Paid" value={n(find(charts.paid_free, "PAID"))} tone="green" />
            <Tile icon={Gift} label="Free" value={n(find(charts.paid_free, "FREE"))} tone="purple" />
            <Tile icon={IndianRupee} label="Revenue" value={rupees(stats.revenue)} tone="yellow" />
          </TileGrid>
        </Section>
        <Section title="Booking Type (Today)">
          <TileGrid cols={3}>
            <Tile icon={CalendarClock} label="Advanced" value={n(find(charts.booking_type, "ADVANCED"))} tone="blue" />
            <Tile icon={CalendarCheck2} label="Current" value={n(find(charts.booking_type, "CURRENT"))} tone="orange" />
            {isSA
              ? <Tile icon={Landmark} label="Temples" value={`${stats.active_temples}/${stats.total_temples}`} tone="red" />
              : <Tile icon={Users} label="Users" value={n(stats.total_users)} tone="red" />}
          </TileGrid>
        </Section>
      </div>

      {darshan.length > 0 && (
        <Section title="Darshan-wise Bookings (Today)">
          <TileGrid cols={darshan.length >= 5 ? 5 : darshan.length === 4 ? 4 : darshan.length === 3 ? 3 : darshan.length}>
            {darshan.map((d, i) => <Tile key={d.name} icon={Sparkles} label={d.name} value={n(d.value)} tone={TONE_CYCLE[i % TONE_CYCLE.length]} />)}
          </TileGrid>
        </Section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section title="Day-wise Transactions" bodyClass="p-3 h-64">
          <ResponsiveContainer>
            <BarChart data={charts.day_wise} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
              {GRID}
              <XAxis dataKey="name" tick={AX} tickLine={false} />
              <YAxis allowDecimals={false} tick={AX} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "#FDF2E9" }} />
              <Bar dataKey="value" name="Transactions" fill="#D7261E" radius={[4, 4, 0, 0]} maxBarSize={34} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Section>
        <Section title={isSA ? "Temple-wise Transactions (Today)" : "Shift / Counter-wise Transactions (Today)"} bodyClass="p-3 h-64">
          <ResponsiveContainer>
            <BarChart data={byPlace} layout="vertical" margin={{ top: 4, right: 12, left: 0, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} tick={AX} />
              <YAxis type="category" dataKey="name" width={110} tick={AX} />
              <Tooltip cursor={{ fill: "#FDF2E9" }} />
              <Bar dataKey="value" name="Transactions" fill="#F07C1A" radius={[0, 4, 4, 0]} maxBarSize={26} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        </Section>
      </div>
    </div>
  );
}
